import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Loader2, Paperclip, Mail } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const EMPTY_BANK = { holder: "", iban: "", bic: "" };

const BankFields = ({ bank, setBank, t }) => (
  <div className="pl-6 space-y-2" data-testid="rib-bank-fields">
    <div className="text-xs font-medium">{t("Coordonnées bancaires affichées dans l'e-mail")}</div>
    <Input value={bank.holder} onChange={(e) => setBank((b) => ({ ...b, holder: e.target.value }))} placeholder={t("Titulaire du compte")} autoComplete="off" data-testid="rib-bank-holder" />
    <Input value={bank.iban} onChange={(e) => setBank((b) => ({ ...b, iban: e.target.value.toUpperCase() }))} placeholder="IBAN" autoComplete="off" className="font-mono" data-testid="rib-bank-iban" />
    <Input value={bank.bic} onChange={(e) => setBank((b) => ({ ...b, bic: e.target.value.toUpperCase() }))} placeholder="BIC" autoComplete="off" className="font-mono" data-testid="rib-bank-bic" />
  </div>
);

// Asks whether to attach a RIB before sending a quote email; onConfirm(ribPath | "", withBank).
export const RibDialog = ({ open, onOpenChange, onConfirm, defaultAttach = false }) => {
  const { t } = useI18n();
  const [saved, setSaved] = useState({ path: "", name: "" });
  const [bank, setBank] = useState(EMPTY_BANK);
  const [savedBank, setSavedBank] = useState(EMPTY_BANK);
  const [attach, setAttach] = useState(false);
  const [other, setOther] = useState(null);
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setOther(null); setMakeDefault(false);
    api.get("/me/rib").then(({ data }) => {
      const b = { ...EMPTY_BANK, ...(data.bank || {}) };
      setSaved(data); setBank(b); setSavedBank(b); setAttach(defaultAttach && (!!data.path || !!b.iban));
    }).catch(() => {});
  }, [open, defaultAttach]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?private=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOther({ path: data.path, name: file.name });
      setAttach(true);
      setMakeDefault(!saved.path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (attach && JSON.stringify(bank) !== JSON.stringify(savedBank)) {
      try { await api.put("/me/bank", bank); } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        return;
      }
    }
    const path = attach ? (other?.path || saved.path) : "";
    if (attach && other && makeDefault) {
      try { await api.put("/me/rib", { path: other.path }); } catch { /* sending still proceeds */ }
    }
    onOpenChange(false);
    onConfirm(path, attach && !!bank.iban.trim());
  };

  const current = other || (saved.path ? saved : null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rib-dialog">
        <DialogHeader>
          <DialogTitle>{t("Envoyer le devis par e-mail")}</DialogTitle>
          <DialogDescription>{t("Vous pouvez joindre un RIB (PDF ou image) \u00e0 l'e-mail.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={attach} onCheckedChange={(v) => setAttach(!!v)} data-testid="rib-attach" />
            <span>{t("Joindre un RIB")}</span>
          </label>
          {current && (
            <div className="text-xs text-muted-foreground pl-6" data-testid="rib-current">
              {other ? t("Fichier choisi") : t("Mon RIB enregistr\u00e9")} : <span className="font-medium text-foreground">{current.name || "RIB"}</span>
            </div>
          )}
          <div className="pl-6">
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="rib-file-input"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="rib-upload-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
              {t(current ? "Joindre un autre RIB" : "Choisir un fichier RIB")}
            </Button>
          </div>
          {other && (
            <label className="flex items-center gap-2 cursor-pointer pl-6 text-xs">
              <Checkbox checked={makeDefault} onCheckedChange={(v) => setMakeDefault(!!v)} data-testid="rib-make-default" />
              <span>{t("Enregistrer comme mon RIB par d\u00e9faut")}</span>
            </label>
          )}
          {attach && <BankFields bank={bank} setBank={setBank} t={t} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rib-cancel">{t("Annuler")}</Button>
          <Button onClick={confirm} disabled={busy} className="gap-1.5" data-testid="rib-confirm-send"><Mail className="w-4 h-4" /> {t("Envoyer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
